/**
 * aspectRatio — compute the intended display aspect ratio of a video file,
 * applying pixel-aspect-ratio (PAR) correction for NTSC/PAL DV masters when
 * the encoded pixel dimensions misrepresent the intended display ratio.
 *
 * See specs/001-mvp-foundation/research.md R5 and the retro-video-presentation-expert
 * skill for the theory. The override menu (FR-025) lets power users override
 * per item when metadata is wrong.
 */

import type { AspectOverride, Item, MetadataFile } from "../../types.ts";

/** Numeric ratios for each override option. "auto" and "native" resolve from the item. */
const OVERRIDE_RATIOS: Record<Exclude<AspectOverride, "auto" | "native">, number> = {
  "4:3": 4 / 3,
  "16:9": 16 / 9,
  "1.37": 1.37,
  "1.85": 1.85,
  "2.35": 2.35,
};

/**
 * Known PAR-correction cases keyed by encoded WxH. These are the classic
 * NTSC/PAL DV rasters where pixels are not square.
 */
const PAR_TABLE: Array<{ w: number; h: number; par: number }> = [
  // NTSC DV 4:3 (720x480 → 10:11 PAR → ~1.33)
  { w: 720, h: 480, par: 10 / 11 },
  // NTSC DV widescreen (720x480 → 40:33 → ~1.82). The default table
  // assumes 4:3; 16:9 NTSC DV is ambiguous from dimensions alone, so we
  // keep the 4:3 default and let the user override.
  // PAL DV 4:3 (720x576 → 12:11 → ~1.36)
  { w: 720, h: 576, par: 12 / 11 },
  // D1/DVCPRO 704x480 NTSC
  { w: 704, h: 480, par: 10 / 11 },
  { w: 704, h: 576, par: 12 / 11 },
];

function encodedRatio(w: number, h: number): number {
  if (!Number.isFinite(w) || !Number.isFinite(h) || h <= 0) return 4 / 3;
  return w / h;
}

/**
 * Compute the intended display aspect ratio of a metadata file.
 * Falls back to the encoded ratio when no PAR correction applies.
 */
export function computeDisplayAspectRatio(file: MetadataFile): number {
  const w = file.width ? parseInt(file.width, 10) : NaN;
  const h = file.height ? parseInt(file.height, 10) : NaN;

  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return 4 / 3; // Safe default for archive.org's heavy 4:3 bias.
  }

  const par = PAR_TABLE.find((e) => e.w === w && e.h === h)?.par;
  if (par !== undefined) {
    return (w * par) / h;
  }

  return encodedRatio(w, h);
}

/**
 * Resolve an aspect ratio to apply given an Item and the current override.
 * - "auto" → the Item's displayAspectRatio (the default, with PAR applied)
 * - "native" → the raw encoded ratio (ignores PAR correction)
 * - numeric overrides → the fixed ratio
 */
export function applyAspectOverride(override: AspectOverride, item: Item): number {
  if (override === "auto") return item.displayAspectRatio;
  if (override === "native") return encodedRatio(item.encodedWidth, item.encodedHeight);
  return OVERRIDE_RATIOS[override];
}
